import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { useVideosData } from "@/hooks/useVideosData";
import { VideoItem, saveToHistory, getHistory } from "@/lib/videosApi";
import {
  Play,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "lucide-react";

export const VideosCourseLessonPage: React.FC = () => {
  const { slug, videoId } = useParams<{ slug: string; videoId: string }>();
  const { videos, collections, loading, error, refetch } = useVideosData();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playedIds, setPlayedIds] = useState<string[]>([]);

  useEffect(() => {
    const history = getHistory();
    setPlayedIds(history.map((h) => h.id));
  }, []);

  const collection = collections.find((c) => c.slug === slug);

  if (loading) {
    return (
      <VideosLayout loading={true}>
        <div />
      </VideosLayout>
    );
  }

  if (!collection) {
    return (
      <VideosLayout error={error} onRetry={refetch}>
        <SEOHead
          title="Lesson Not Found | JNN Homes Northern Virginia"
          description="The requested video lesson could not be found."
          noIndex={true}
        />
        <div className="p-12 text-center rounded-2xl bg-slate-50 border border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Course not found
          </h1>
          <Link
            to="/videos/playlists"
            className="text-sm font-semibold text-[#9A5207] hover:underline"
          >
            Browse all playlists
          </Link>
        </div>
      </VideosLayout>
    );
  }

  // Ordered videos for this collection
  const courseVideos = collection.lessons
    .map((id) => videos.find((v) => v.id === id))
    .filter((v): v is NonNullable<VideoItem> => Boolean(v));

  const currentIndex = courseVideos.findIndex((v) => v.id === videoId);
  const currentVideo = currentIndex >= 0 ? courseVideos[currentIndex] : null;
  const prevVideo = currentIndex > 0 ? courseVideos[currentIndex - 1] : null;
  const nextVideo =
    currentIndex < courseVideos.length - 1
      ? courseVideos[currentIndex + 1]
      : null;

  if (!currentVideo) {
    return (
      <VideosLayout error={error} onRetry={refetch}>
        <SEOHead
          title="Lesson Not Found | JNN Homes Northern Virginia"
          description="The requested video lesson could not be found."
          noIndex={true}
        />
        <div className="p-12 text-center rounded-2xl bg-slate-50 border border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Lesson not found
          </h1>
          <Link
            to={`/videos/course/${slug}`}
            className="text-sm font-semibold text-[#9A5207] hover:underline"
          >
            Back to course
          </Link>
        </div>
      </VideosLayout>
    );
  }

  const handlePlay = () => {
    setIsPlaying(true);
    saveToHistory({
      id: currentVideo.id,
      t: currentVideo.title,
      href: `/videos/course/${slug}/${currentVideo.id}`,
    });
    // mark as played
    setPlayedIds((prev) => [...prev, currentVideo.id]);
  };

  const canonicalUrl = `https://www.jnnhomes.com/videos/course/${slug}/${videoId}`;

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title={`${currentVideo.title} | ${collection.title} | JNN Homes Northern Virginia`}
        description={collection.description}
        canonical={canonicalUrl}
      />

      <div className="space-y-6">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium flex-wrap">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <Link to="/videos/playlists" className="hover:text-[#9A5207]">
            Playlists
          </Link>
          <span>›</span>
          <Link to={`/videos/course/${slug}`} className="hover:text-[#9A5207]">
            {collection.title}
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold truncate">
            {currentVideo.title}
          </span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6">
          {/* Player + Meta */}
          <div className="space-y-4 min-w-0">
            {/* 9:16 Player */}
            <div
              className="relative w-full overflow-hidden rounded-2xl bg-slate-900"
              style={{ maxHeight: "min(74vh, 720px)" }}
            >
              {isPlaying ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${currentVideo.id}?autoplay=1&rel=0&playsinline=1`}
                  title={currentVideo.title}
                  className="w-full border-0"
                  style={{
                    height: "min(74vh, 720px)",
                    aspectRatio: "9 / 16",
                    margin: "0 auto",
                    display: "block",
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div
                  className="relative w-full bg-slate-200 cursor-pointer group"
                  style={{
                    height: "min(74vh, 720px)",
                    aspectRatio: "9 / 16",
                    margin: "0 auto",
                  }}
                  onClick={handlePlay}
                >
                  <img
                    src={`https://i.ytimg.com/vi/${currentVideo.id}/hqdefault.jpg`}
                    alt={`Thumbnail for ${currentVideo.title}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                    <button
                      onClick={handlePlay}
                      aria-label={`Play video: ${currentVideo.title}`}
                      className="w-16 h-16 rounded-full bg-[#E8850F] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                    >
                      <Play className="w-7 h-7 fill-white ml-1" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Meta below player */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                <Link
                  to={`/videos/course/${slug}`}
                  className="hover:text-[#9A5207]"
                >
                  {collection.title}
                </Link>
                <span>·</span>
                <span>
                  Lesson {currentIndex + 1} of {courseVideos.length}
                </span>
              </div>
              <h1 className="text-xl font-extrabold text-slate-900 leading-snug">
                {currentVideo.title}
              </h1>
              <div className="flex items-center gap-3 text-xs text-slate-500 font-medium">
                {currentVideo.date && (
                  <time dateTime={currentVideo.date}>
                    {new Date(currentVideo.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </time>
                )}
                <span>·</span>
                <span>{currentVideo.duration}</span>
                <span>·</span>
                <span className="capitalize">{currentVideo.category}</span>
              </div>

              <a
                href={`https://www.youtube.com/watch?v=${currentVideo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#9A5207] hover:underline"
              >
                Watch on YouTube <ExternalLink className="w-3.5 h-3.5" />
              </a>

              {/* Prev / Next buttons */}
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                {prevVideo ? (
                  <Link
                    to={`/videos/course/${slug}/${prevVideo.id}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:border-[#E8850F] hover:text-[#9A5207] transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </Link>
                ) : (
                  <span />
                )}
                {nextVideo ? (
                  <Link
                    to={`/videos/course/${slug}/${nextVideo.id}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#B35A06] text-white text-xs font-semibold hover:bg-[#9A5207] transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <span />
                )}
              </div>
            </div>
          </div>

          {/* Sticky Lesson List */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 shadow-xs">
              <h2 className="text-sm font-bold text-slate-900">
                Lessons in this course
              </h2>
              <ol className="space-y-1.5">
                {courseVideos.map((video, idx) => {
                  const active = video.id === currentVideo.id;
                  const played = playedIds.includes(video.id);
                  return (
                    <li key={video.id}>
                      <Link
                        to={`/videos/course/${slug}/${video.id}`}
                        aria-current={active ? "true" : undefined}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] ${
                          active ? "bg-[#E8850F]/14" : "hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                            played
                              ? "bg-[#E8850F] text-white"
                              : active
                                ? "bg-[#9A5207]/14 text-[#9A5207]"
                                : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {played ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                        </span>
                        <span
                          className={`text-xs font-medium truncate ${
                            active
                              ? "text-[#9A5207] font-semibold"
                              : "text-slate-700"
                          }`}
                        >
                          {video.title}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </VideosLayout>
  );
};
